import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './styles.css';

/**
 * DeliverablesWarningModal (Shared)
 * 
 * Shows when user tries to mark a task as complete when deliverables are not submitted.
 * Similar to DependencyWarningModal but focused on deliverables.
 * 
 * Features:
 * - Lists incomplete deliverables
 * - Offers option to submit deliverables first
 * - Option to open the mission to see/submit deliverables
 * - Option to override and complete anyway
 * 
 * @param {boolean} isOpen - Whether the modal is visible
 * @param {Array} incompleteDeliverables - Array of { id, name, fileType } objects
 * @param {number} totalDeliverables - Total count of deliverables (for X/Y display)
 * @param {string} taskName - Name of the task being completed
 * @param {function} onConfirm - Callback when user confirms completing despite warning
 * @param {function} onCancel - Callback when user cancels
 * @param {function} onSubmitDeliverable - Optional callback(deliverable) to open submit modal
 * @param {function} onOpenMissionView - Optional callback to open the mission
 */
const DeliverablesWarningModal = ({
  isOpen,
  incompleteDeliverables = [],
  totalDeliverables = 0,
  taskName = 'this mission',
  onConfirm,
  onCancel,
  onSubmitDeliverable,
  onOpenMissionView,
}) => {
  const cancelBtnRef = useRef(null);

  useEffect(() => {
    if (isOpen && cancelBtnRef.current) {
      cancelBtnRef.current.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onCancel?.();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onCancel?.();
    }
  };

  const handleDeliverableClick = (deliverable) => {
    if (onSubmitDeliverable) {
      onCancel?.();
      onSubmitDeliverable(deliverable);
    }
  };

  const isSingle = incompleteDeliverables.length === 1;
  const delName = isSingle ? incompleteDeliverables[0]?.name : null;
  const hasSubmit = !!onSubmitDeliverable;
  const hasOpenMission = !!onOpenMissionView;
  
  // Calculate completed count for display (X/Y format)
  const total = totalDeliverables || incompleteDeliverables.length;
  const completedCount = total - incompleteDeliverables.length;

  const handleOpenMissionView = () => {
    onCancel?.();
    onOpenMissionView?.();
  };

  const modal = (
    <div 
      className="del-warning-modal__backdrop" 
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <div className="del-warning-modal__container">
        {/* Icon */}
        <div className="del-warning-modal__emoji">📦</div>

        {/* Content */}
        <div className="del-warning-modal__content">
          <h3 className="del-warning-modal__title">
            {isSingle ? "Missing a deliverable!" : "Missing deliverables!"}
          </h3>
          
          <p className="del-warning-modal__message">
            {isSingle 
              ? <>Before marking <strong>"{taskName}"</strong> as complete, you need to submit <strong>"{delName}"</strong>.</>
              : <>Before marking <strong>"{taskName}"</strong> as complete, you need to submit these {incompleteDeliverables.length} deliverables.</>
            }
          </p>

          {/* Deliverables list */}
          <div className="del-warning-modal__items-section">
            {hasSubmit && (
              <p className="del-warning-modal__items-hint">
                (Click any deliverable below to submit it now)
              </p>
            )}
            <div className="del-warning-modal__items">
              {incompleteDeliverables.map((del) => (
                <button
                  key={del.id}
                  className={`del-warning-modal__item ${hasSubmit ? 'del-warning-modal__item--clickable' : ''}`}
                  onClick={() => handleDeliverableClick(del)}
                  disabled={!hasSubmit}
                >
                  <span className="del-warning-modal__item-checkbox">○</span>
                  <span className="del-warning-modal__item-name">{del.name}</span>
                  {del.fileType && (
                    <span className="del-warning-modal__item-type">{del.fileType}</span>
                  )}
                  {hasSubmit && (
                    <svg className="del-warning-modal__item-arrow" viewBox="0 0 16 16" fill="none">
                      <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="del-warning-modal__footer">
          {hasOpenMission && (
            <button 
              ref={cancelBtnRef}
              className="del-warning-modal__btn del-warning-modal__btn--primary"
              onClick={handleOpenMissionView}
            >
              📄 Open Mission ({completedCount}/{total})
            </button>
          )}
          
          {!hasOpenMission && (
            <button 
              ref={cancelBtnRef}
              className="del-warning-modal__btn del-warning-modal__btn--primary"
              onClick={onCancel}
            >
              {hasSubmit ? 'Submit deliverables first' : 'Got it'}
            </button>
          )}
          
          <div className="del-warning-modal__skip-section">
            <p className="del-warning-modal__skip-hint">
              Or if you want to complete without submitting:
            </p>
            <button 
              className="del-warning-modal__btn del-warning-modal__btn--secondary"
              onClick={onConfirm}
            >
              Mark as complete anyway
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default DeliverablesWarningModal;
